import { specialWords, type BuildingTypeKey, type ProcessStepDto } from '@nsb/core';
import { useState } from 'react';
import { Link } from 'react-router';
import { errorText } from '../../api.ts';
import { ErrorLine } from '../ui.tsx';
import { buildingRows, coveredLine, poolLine, toggled } from './buildingTypes.ts';
import { processAction } from './useProcess.ts';

/**
 * What is being built (spec "Pattern Mapping" §4.2; Jason, 2026-10-16: "are we building a workshop? Are we building a
 * house? And then it'll just populate the available patterns for them to pick"): on the setup page, a step carrying
 * Pattern mapping is ticked with any of the building types, each tick held in the job's draft with the whole set; every
 * pattern those buildings could touch is the language's to pick from. Elsewhere it reads as words, set on the setup page;
 * once the language is locked, it is locked with it and reads as words everywhere (spec §3.3), changing only if the
 * language is opened again, which a job kept as it ended never is.
 */
export function BuildingTypes({ projectId, step, edits, kept }: { projectId: string; step: ProcessStepDto; edits: boolean; kept: boolean }) {
  // What was just ticked, shown at once while the draft takes it; the step as the draft has it once it answers.
  const [pending, setPending] = useState<BuildingTypeKey[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const special = step.specials?.find((x) => x.key === 'patterns');
  if (!special) return null;
  const types = pending ?? special.settings?.types ?? [];

  if (!edits || special.done) {
    const said = specialWords('patterns', special.settings);
    return (
      <div className="border-t border-rule py-3">
        <div className="label mb-1.5">What is being built</div>
        <p className="text-sm">
          {`${said.charAt(0).toUpperCase()}${said.slice(1)}.`}
          {/* Kept as it ended, the language is never opened again: locked, it is kept so. */}
          {special.done && kept ? (
            ' Locked with the language.'
          ) : special.done ? (
            <>
              {' '}
              Locked with the language: opened again on{' '}
              <Link to={`/p/${projectId}/patterns`} className="link">
                Patterns
              </Link>
              , it can change.
            </>
          ) : kept ? null : (
            <>
              {' '}
              <Link to={`/p/${projectId}/settings/process`} className="link">
                Set on the setup page
              </Link>
              .
            </>
          )}
        </p>
      </div>
    );
  }

  const send = async (next: BuildingTypeKey[]) => {
    setPending(next);
    setErr(null);
    try {
      await processAction(projectId, `/steps/${step.id}/special`, { key: 'patterns', settings: { types: next } });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setPending(null);
    }
  };
  const covered = coveredLine(types);
  return (
    <div className="border-t border-rule py-3">
      <fieldset disabled={pending !== null}>
        <legend className="label mb-1.5 block">What is being built</legend>
        <p className="text-sm">Tick what is being built: every pattern those buildings could touch is there to pick from.</p>
        <div className="mt-2 border-y border-rule">
          {buildingRows(types).map((r) => (
            <label key={r.key} className="flex min-h-11 cursor-pointer items-baseline gap-3 border-b border-rule py-3 last:border-b-0">
              <input type="checkbox" className="relative top-0.5 size-4 shrink-0 accent-moss" checked={r.ticked} onChange={(e) => void send(toggled(types, r.key, e.target.checked))} />
              <span className="flex-1">{r.name}</span>
              <span className="num text-sm text-ink-3">{r.count} patterns</span>
            </label>
          ))}
        </div>
      </fieldset>
      <p className="meta mt-2">{poolLine(types, step.state === 'done')}</p>
      {covered ? <p className="meta mt-1">{covered}</p> : null}
      <ErrorLine error={err} />
    </div>
  );
}
